/**
 * Table Manager
 * 
 * Responsible for creating, verifying, and managing database tables
 */

import tablesMetadataService from '../tablesMetadataService';
import { 
    DB_CONSTANTS, 
    getDynamicTables, 
    saveDynamicTables, 
    isTableBeingCreated, 
    markTableAsBeingCreated, 
    unmarkTableAsBeingCreated, 
    incrementVersion, 
    getTableSchema, 
    createDynamicStores
} from './dbStorageHelper';

class TableManager {
    constructor() {
        this.dynamicTables = getDynamicTables();
        this.pendingTableCreation = new Map();
    }

    getAllRequiredTables() {
        const tables = new Set();
        
        // Include all syncable tables from tablesMetadataService
        tablesMetadataService.GetAllSyncableTables().forEach(tableName => tables.add(tableName));
        
        // Include dynamic tables that were created during synchronization
        Object.keys(this.dynamicTables).forEach(tableName => tables.add(tableName));
        
        return Array.from(tables);
    }

    createDynamicStores(db) {
        // Use helper function
        createDynamicStores(db, this.dynamicTables);
    }

    async ensureTableExists(db, tableName, initializeCallback) {
        // If this table is currently being created in another async operation, wait for it
        if (isTableBeingCreated(tableName) && this.pendingTableCreation.has(tableName)) {
            try {
                await this.pendingTableCreation.get(tableName);
                return true;
            } catch (error) {
                console.error(`Error waiting for table ${tableName} creation:`, error);
                // Continue with our own attempt
            }
        }
        
        // Check if table exists
        if (!db.objectStoreNames.contains(tableName)) {
            
            // Add to the set of tables being created
            markTableAsBeingCreated(tableName);
            
            // Create a promise that will be resolved when the table is created
            const creationPromise = new Promise(async (resolve, reject) => {
                try {
                    // Record this table as a dynamic table for future app launches
                    this.dynamicTables[tableName] = {
                        created: new Date().toISOString(),
                        indexes: ['isDeleted'] // Default indexes
                    };
                    saveDynamicTables(this.dynamicTables);

                    // Close current connection
                    db.close();
                    
                    // Increment version and reinitialize
                    const newVersion = incrementVersion();
                    
                    // Reinitialize with new version
                    await initializeCallback();
                    
                    // Check if table was actually created
                    if (!db.objectStoreNames.contains(tableName)) {
                        throw new Error(`Failed to create table ${tableName} during database upgrade`);
                    }
                    
                    resolve(true);
                } catch (error) {
                    console.error(`Error creating table ${tableName}:`, error);
                    unmarkTableAsBeingCreated(tableName);
                    reject(error);
                }
            });
            
            // Store the promise
            this.pendingTableCreation.set(tableName, creationPromise);
            
            try {
                // Wait for the table to be created
                await creationPromise;
                // After promise resolves, we can clear it
                this.pendingTableCreation.delete(tableName);
                return true; // Indicates database was upgraded
            } catch (error) {
                this.pendingTableCreation.delete(tableName);
                throw error;
            }
        }
        
        // Special case for SystemConfig: check if category_name index exists
        if (tableName === 'SystemConfig') {
            try {
                const transaction = db.transaction([tableName], 'readonly');
                const store = transaction.objectStore(tableName);
                
                // Try to access the index to see if it exists
                let indexExists = false;
                try {
                    store.index('category_name');
                    indexExists = true;
                } catch (error) {
                    // Index doesn't exist
                    indexExists = false;
                }
                
                // If index doesn't exist, we need to upgrade the database
                if (!indexExists) {
                    
                    // Close current connection
                    db.close();
                    
                    // Increment version and reinitialize
                    const newVersion = incrementVersion(); 
                    
                    // Reinitialize with new version
                    await initializeCallback();
                    return true; // Indicates database was upgraded
                }
            } catch (error) {
                console.error('Error checking for SystemConfig index:', error);
                // Continue without upgrading
            }
        }
        
        return false; // Indicates no upgrade was needed
    }

    // Add a direct function that explicitly checks if a table exists without attempting a transaction
    tableExists(db, tableName) {
        if (!db) return false;
        return db.objectStoreNames.contains(tableName);
    }

    // Add a more direct table creation function for emergency cases
    async createEmergencyTable(db, tableName, initializeCallback) {
        try {
            // Record this table as a dynamic table with the appropriate schema
            const schema = getTableSchema(tableName);

            const dynamicTables = getDynamicTables();
            dynamicTables[tableName] = {
                created: new Date().toISOString(),
                schema: schema,
                indexes: schema.indexes.map(idx => idx.name),
                emergency: true
            };
            saveDynamicTables(dynamicTables);

            // Check if db exists before trying to close it
            if (db) {
                try {
                    db.close();
                } catch (closeError) {
                    console.warn(`[dbStorageService] Error closing database: ${closeError.message}`);
                }
            }
            
            // Increment version and reinitialize
            incrementVersion();
            
            // Reinitialize with new version
            await initializeCallback();
            
            // Check if initialization succeeded
            if (!db) {
                throw new Error(`Failed to initialize database after version increment`);
            }
            
            // Verify the table was created with correct schema
            if (!db.objectStoreNames.contains(tableName)) {
                throw new Error(`EMERGENCY: Failed to create table ${tableName} even after forced attempt`);
            }
            
            // Verify the schema was applied correctly
            const transaction = db.transaction(tableName, 'readonly');
            const store = transaction.objectStore(tableName);
            if (store.keyPath !== schema.keyPath) {
                throw new Error(`Table ${tableName} created with incorrect keyPath: ${store.keyPath}, expected: ${schema.keyPath}`);
            }
            
            return true;
        } catch (error) {
            console.error(`[dbStorageService] Error in createEmergencyTable for ${tableName}:`, error);
            // Return false instead of throwing to allow the application to continue
            return false;
        }
    }

    getSyncableTables() {
        return tablesMetadataService.GetAllSyncableTables();
    }

    async getLocalTables(db) {
        const tables = {};
        const storeNames = Array.from(db.objectStoreNames);
        
        for (const storeName of storeNames) {
            const recordCount = await this.getRecordCount(db, storeName);
            tables[storeName] = {
                displayName: storeName,
                description: `Local IndexedDB store with ${recordCount} records`,
                recordCount
            };
        }
        
        return { tablesSummary: tables };
    }

    async getRecordCount(db, tableName) {
        const transaction = db.transaction(tableName, 'readonly');
        const store = transaction.objectStore(tableName);

        return new Promise((resolve, reject) => {
            const request = store.count();
            request.onsuccess = () => resolve(request.result);
            request.onerror = () => reject(request.error);
        });
    }

    /**
     * Verifies and creates required indexes on all tables
     * Ensures uuid, lastModified, and isDeleted indexes exist
     */
    async ensureRequiredIndexes(db, initializeCallback) {
        try {
            if (!db) return;
            
            const tables = Array.from(db.objectStoreNames);
            // console.log(`[dbStorageService] Verifying indexes for ${tables.length} tables`);
            
            // Import the ensureCriticalIndexes function dynamically to avoid circular dependencies
            const { ensureCriticalIndexes } = await import('./indexedDBUtils');
            
            // Check and create indexes as needed
            const indexesCreated = await ensureCriticalIndexes(DB_CONSTANTS.DB_NAME, tables);
            
            if (indexesCreated) {
                // console.log('[dbStorageService] Indexes were created, re-initializing database connection');
                
                // Re-initialize the database connection if indexes were created
                if (db) {
                    db.close();
                }
                
                // Re-initialize
                await initializeCallback();
                return true;
            }
            
            return false;
        } catch (error) {
            console.error('[dbStorageService] Error ensuring required indexes:', error);
            // Continue initialization even if index creation fails
            return false;
        }
    }
}

export default new TableManager(); 